import React, { useEffect, useState } from 'react'
import { useLocation, useNavigate, useOutletContext } from 'react-router-dom'
import { Box, Typography, Stack } from '@mui/material'
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined'
import BarChartOutlinedIcon from '@mui/icons-material/BarChartOutlined'
import { CommonTabs, CommonButton } from '@/components/common'
import { ResultCard } from '@/components/donation'
import { DrivesSectionHeader } from '@/components/chairperson/drives-section-header'
import { PastDriveCard } from '@/components/chairperson/past-drive-card'
import { useGetUpcomingDrives, useGetPastDrives } from '@/hooks'
import { buildResultItem, formatIsoDateWithWeekday } from '@/utils'
import { ROUTES } from '@/constants'
import { ResultCardSkeleton } from '@/pages/donate-near-you/results/Skeletons'

export const ChairpersonDashboardPage = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { selectedAccountId } = useOutletContext()
  const [activeTab, setActiveTab] = useState(
    location.state?.tab === 'past' ? 1 : 0
  )
  const { upcomingDrives, loadingUpcomingDrives, getUpcomingDrives } =
    useGetUpcomingDrives()
  const {
    pastDrives,
    loadingPastDrives,
    loadingMorePastDrives,
    hasMorePastDrives,
    getPastDrives,
    loadMorePastDrives,
  } = useGetPastDrives()

  const handleSelectDrive = item => {
    const driveId = item?.operation_id
    if (driveId != null) {
      navigate(`/chairperson/schedule-drive/${driveId}`, {
        state: { operationType: item?.operation_type ?? 'bloodmobile' },
      })
    }
  }

  const handleViewResults = (item, fromTab = 'past') => {
    const driveId = item?.operation_id
    if (driveId != null) {
      navigate(`/chairperson/drive-results/${driveId}`, {
        state: { accountId: selectedAccountId, fromTab },
      })
    }
  }

  const handleViewDonors = (item, fromTab) => {
    const driveId = item?.operation_id
    if (driveId == null) return

    navigate(ROUTES.CHAIRPERSON_DRIVE_DONORS.replace(':driveId', driveId), {
      state: {
        accountId: selectedAccountId,
        driveName: item?.name,
        driveDate: item?.date,
        fromTab,
      },
    })
  }

  useEffect(() => {
    getUpcomingDrives(selectedAccountId)
    getPastDrives(selectedAccountId, 1)
  }, [selectedAccountId])

  const upcomingResultItems = (upcomingDrives ?? []).map(schedule =>
    buildResultItem(schedule, {})
  )

  const pastResultItems = (pastDrives ?? []).map(schedule =>
    buildResultItem(schedule, {})
  )

  const upcomingContent = !selectedAccountId ? (
    <Typography variant='body2' color='text.secondary'>
      Select an account to view upcoming drives.
    </Typography>
  ) : (
    <Stack spacing={2}>
      <DrivesSectionHeader
        title='Upcoming Drives'
        icon={CalendarMonthOutlinedIcon}
        description='Select a drive below to view donor schedules and availability.'
      />
      {loadingUpcomingDrives ? (
        Array.from({ length: 5 }).map((_, index) => (
          <ResultCardSkeleton key={`skeleton-${index}`} />
        ))
      ) : upcomingResultItems.length === 0 ? (
        <Typography variant='body1' color='text.secondary'>
          No upcoming drives found.
        </Typography>
      ) : (
        upcomingResultItems.map((item, index) => (
          <ResultCard
            key={item.id ?? index}
            item={item}
            operationDate={
              item.date ? formatIsoDateWithWeekday(item.date) : undefined
            }
            compactActions
            onSelect={() => handleSelectDrive(item)}
            onViewDonors={() => handleViewDonors(item, 'upcoming')}
            onViewResults={() => handleViewResults(item, 'upcoming')}
          />
        ))
      )}
    </Stack>
  )

  const pastContent = !selectedAccountId ? (
    <Typography variant='body2' color='text.secondary'>
      Select an account to view past drives.
    </Typography>
  ) : (
    <Stack spacing={2}>
      <DrivesSectionHeader
        title='Past Drives'
        icon={BarChartOutlinedIcon}
        description='View drive performance and donor details from completed drives.'
      />
      {loadingPastDrives ? (
        Array.from({ length: 3 }).map((_, index) => (
          <ResultCardSkeleton key={`past-skeleton-${index}`} />
        ))
      ) : pastResultItems.length === 0 ? (
        <Typography variant='body1' color='text.secondary'>
          No completed blood drives were found for this organization.
        </Typography>
      ) : (
        <>
          {pastResultItems.map((item, index) => (
            <PastDriveCard
              key={item.operation_id ?? index}
              item={item}
              onViewResults={() => handleViewResults(item)}
              onViewDonors={() => handleViewDonors(item, 'past')}
            />
          ))}
          {loadingMorePastDrives &&
            Array.from({ length: 3 }).map((_, index) => (
              <ResultCardSkeleton key={`past-more-skeleton-${index}`} />
            ))}
          {hasMorePastDrives && !loadingMorePastDrives && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
              <CommonButton
                variant='secondary'
                onClick={() => loadMorePastDrives(selectedAccountId)}
              >
                Load More Past Drives
              </CommonButton>
            </Box>
          )}
        </>
      )}
    </Stack>
  )

  return (
    <Box>
      <CommonTabs
        id='chairperson-drives'
        value={activeTab}
        onChange={(e, value) => setActiveTab(value)}
        tabs={[
          { label: 'Upcoming Drives', content: upcomingContent },
          { label: 'Past Drives', content: pastContent },
        ]}
      />
    </Box>
  )
}
